import {
  ChangeDetectorRef,
  Directive,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { Router } from '@angular/router';
import { Lightbox } from 'ngx-lightbox';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import ProductAsset from 'src/app/model/product-asset';
import { BoardTypeChoices } from 'src/app/model/product/board/board-part';
import { ProperPageResult } from 'src/app/model/proper-page-result';
import { FilterEnabledComponent } from '../../../helpers/filter-enabled.component';
import { FilterObject } from '../../../helpers/filter-object';
import Asset from '../../../model/asset';
import { SubProjectControllerRequestService } from '../../../services/api/sub-project-controller-request.service';
import { FilterStoreService } from '../../../services/filter-store.service';
import {
  PageIndexService,
  PAGE_SIZE_OPTIONS_IMMO,
} from '../../../services/page-index-service';
import * as moment from 'moment';
import DateHelper from '../../../helpers/date-helper';
import { FormatAppointmentTooltipPipe } from '../../../pipes/format-appointment-tooltip.pipe';

@Directive()
export abstract class AbstractImmoOverviewComponent<T>
  extends FilterEnabledComponent
  implements OnInit, OnDestroy
{
  @Output()
  reloadCountChange = new EventEmitter<void>();

  protected amountRetrievedForPage = new Subject<number>();

  protected readonly today: moment.Moment = DateHelper.getMoment();

  pageSizeOptions = PAGE_SIZE_OPTIONS_IMMO;
  BOARD_TYPE_CHOICES = BoardTypeChoices;

  formatAppointmentTooltip: FormatAppointmentTooltipPipe;

  loading: boolean;
  totalItems = 0;
  items: T[];

  displayedColumns: string[];

  subProjectTypeRightMargin = false;

  sortColumn: string = 'project.currentRequestDate';
  sortAscending: boolean = false;

  hasSaveFunctionality: boolean = false;

  fixedSubProjectTypeLabel = null;
  requestAdjustmentTooltipSuffix = 'aanvragen';

  showDelete = true;
  showRelease = false;
  showReleaseFromDelay = false;
  showInvoiceAsNonExecuted = false;

  placersLoaded: boolean = false;
  hasActionButtons: boolean = false;

  constructor(
    displayedFilters: any[],
    protected filterStoreService: FilterStoreService,
    protected pageIndexService: PageIndexService,
    protected cd: ChangeDetectorRef,
    protected subProjectService: SubProjectControllerRequestService,
    protected _lightbox: Lightbox,
    protected router: Router,
    public showCollaborativeIcon = false
  ) {
    super(displayedFilters, filterStoreService, pageIndexService);
  }

  ngOnInit() {
    this.initDisplayedColumns();
    this.initSortSettings();
    this.initPageSettings();
    this.initSubscriptions();

    this.fetchItems();

    this.filtersChanged.pipe(debounceTime(800)).subscribe(() => {
      this.resetPage();
    });
  }

  private resetPage() {
    this.pageIndexService.setPageIndex(this.getLocalStorageKey(), 0);
    this.getPaginator().pageIndex = 0;
    this.fetchItems();
  }

  private initSubscriptions(): void {
    this.addSubscription(this.amountRetrievedForPage, (amount: number) => {
      if (amount <= 0 && this.getPaginator().pageIndex > 0) {
        this.resetPage();
      }
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.amountRetrievedForPage != null) {
      this.amountRetrievedForPage.complete();
      this.amountRetrievedForPage = null;
    }
  }

  private initPageSettings() {
    const pageIndex = this.pageIndexService.getPageIndex(
      this.getLocalStorageKey()
    );
    const pageSize = this.pageIndexService.getPageSize(
      this.getLocalStorageKey()
    );

    this.getPaginator().pageIndex = pageIndex != null ? pageIndex : 0;
    this.initializePageSize(pageSize);
  }

  protected initializePageSize(localStoredPageSize: number): void {
    this.getPaginator().pageSize =
      localStoredPageSize != null ? localStoredPageSize : 200;
  }

  //Slightly weird workaround: paginator isn't instantiated (in time) if we define it in the abstract class. So we have to define it in the class itself
  //and fetch it this way
  protected abstract getPaginator(): MatPaginator;

  protected abstract initDisplayedColumns(): void;

  protected abstract initSortSettings(): void;

  protected abstract fetchItems(): void;

  public abstract openProject(subProject: T);

  protected processPageResult(pageResult: ProperPageResult<T>): void {
    if (pageResult == null || pageResult.items == null) {
      this.amountRetrievedForPage.next(0);
    } else {
      this.amountRetrievedForPage.next(pageResult.items.length);
    }
  }

  public abstract openTour(subProject: T): void;

  public getFilterForKey(key: string): FilterObject {
    return super.getFilterForKey(key);
  }

  public toggleFilter(filterName: string): void {
    super.toggleFilter(filterName);
  }

  public onFilterValueChange(filterData: any): void {
    super.onFilterValueChange(filterData);
  }

  public openLightbox(asset: Asset): void {
    let albums = [];

    const src = '/api/assets/' + asset.uuid;
    const caption = 'Design';
    const thumb = '/api/assets/' + asset.uuid;
    const album = {
      src: src,
      caption: caption,
      thumb: thumb,
    };

    albums.push(album);
    this._lightbox.open(albums, 0);
  }

  public openPhotosLightbox(photoModel: PhotoModel): void {
    const albums = [];

    for (const photo of photoModel.photos) {
      if (photo.asset != null) {
        albums.push({
          src: `/api/assets/${photo.asset.uuid}`,
          caption: photo.formSection,
          thumb: `/api/assets/${photo.asset.uuid}`,
        });
      }
    }

    this._lightbox.open(albums, 0);
  }

  public onPageChange() {
    this.pageIndexService.setPageIndex(
      this.getLocalStorageKey(),
      this.getPaginator().pageIndex
    );
    this.pageIndexService.setPageSize(
      this.getLocalStorageKey(),
      this.getPaginator().pageSize
    );

    this.fetchItems();
  }

  public abstract copyProjectId(subProject: T): void;
}

interface PhotoModel {
  photos: ProductAsset[];
}
